import { Actor, Canvas, Engine, Random, vec } from 'excalibur';
import { ThemePaletteKey, getTheme } from '../themes/theme';
import { approach, normalizeDegrees, toRadians } from '../utils/angles';
import { SEEDS, SPEED_CAP, SPEED_GAIN } from '../utils/constants';
import { css, drawTaperedStroke, wobblyCirclePath } from '../utils/sketch';

export interface ClockHandOptions {
  name: string;
  length: number;
  thickness: number;
  bodyTone: ThemePaletteKey;
  edgeTone: ThemePaletteKey;
  direction: 1 | -1;
  baseSpeed: number;
  seed: number;
}

export class ClockHand extends Actor {
  readonly direction: 1 | -1;
  readonly baseSpeed: number;
  readonly length: number;
  angleDeg = 0;
  private growth = 1;
  private hotTarget = 0;
  private hotAmount = 0;
  private time = 0;
  private readonly glow: Actor;
  private readonly handCanvas: Canvas;
  private readonly glowCanvas: Canvas;

  constructor(options: ClockHandOptions) {
    const padX = options.thickness * 2.4 + 16;
    const padTop = 34;
    const padBottom = 30;
    const width = padX * 2;
    const height = options.length + padTop + padBottom;
    const pivotY = height - padBottom;
    const handCanvas = new Canvas({
      width,
      height,
      cache: true,
      draw: (ctx) => paintHand(ctx, options, width, pivotY),
    });
    super({
      name: options.name,
      graphic: handCanvas,
    });
    this.handCanvas = handCanvas;
    this.length = options.length;
    this.direction = options.direction;
    this.baseSpeed = options.baseSpeed;
    this.anchor = vec(0.5, pivotY / height);

    const glowCanvas = new Canvas({
      width: 80,
      height: 80,
      cache: true,
      draw: (ctx) => paintTipGlow(ctx, options.bodyTone),
    });
    this.glowCanvas = glowCanvas;
    this.glow = new Actor({
      name: `${options.name}-glow`,
      pos: vec(0, -options.length),
      graphic: glowCanvas,
    });
    this.glow.graphics.opacity = 0;
    this.addChild(this.glow);
  }

  get speedMultiplier(): number {
    return this.growth;
  }

  reset(): void {
    this.growth = 1;
    this.hotAmount = 0;
    this.hotTarget = 0;
    this.glow.graphics.opacity = 0;
  }

  grow(): void {
    this.growth = Math.min(SPEED_CAP, this.growth * SPEED_GAIN);
  }

  spin(dtSeconds: number, multiplier: number): void {
    const speed = this.baseSpeed * this.growth * this.direction;
    this.angleDeg = normalizeDegrees(this.angleDeg + speed * multiplier * dtSeconds);
    this.rotation = toRadians(this.angleDeg);
  }

  addWobble(degrees: number): void {
    this.rotation = toRadians(this.angleDeg + degrees);
  }

  setHot(hot: boolean): void {
    this.hotTarget = hot ? 1 : 0;
  }

  refreshTheme(): void {
    this.handCanvas.flagDirty();
    this.glowCanvas.flagDirty();
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    this.hotAmount = approach(this.hotAmount, this.hotTarget, dt * 7);
    const pulse = 1 + Math.sin(this.time * 5.2) * 0.12 * this.hotAmount;
    const scale = (0.85 + this.hotAmount * 0.5) * pulse;
    this.glow.scale = vec(scale, scale);
    this.glow.graphics.opacity = this.hotAmount * 0.9;
  }
}

function paintHand(
  ctx: CanvasRenderingContext2D,
  options: ClockHandOptions,
  width: number,
  pivotY: number,
): void {
  const rng = new Random(options.seed);
  const palette = getTheme().palette;
  const cx = width / 2;
  drawTaperedStroke(
    ctx,
    rng,
    cx,
    pivotY + 12,
    pivotY - options.length + 3,
    options.thickness,
    palette[options.bodyTone],
    palette[options.edgeTone],
  );
}

function paintTipGlow(ctx: CanvasRenderingContext2D, tone: ThemePaletteKey): void {
  const rng = new Random(SEEDS.glow);
  const palette = getTheme().palette;
  const color = palette[tone];
  const mid = 40;
  ctx.save();
  for (let i = 6; i >= 1; i--) {
    ctx.fillStyle = css(color, 0.045 + (6 - i) * 0.012);
    wobblyCirclePath(ctx, mid, mid, 12 + i * 4.6, rng, 1.6, 26);
    ctx.fill();
  }
  ctx.fillStyle = css(palette.cream, 0.22);
  wobblyCirclePath(ctx, mid, mid, 12, rng, 1, 18);
  ctx.fill();
  ctx.restore();
}
